import { Box, Paper, Slider } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useAppSelector } from "hooks/store";
import { TextFieldInput } from "new-components/textFieldInput/TextFieldInput";
import { getUserState } from "store/slices/Auth/authSlice";
import { DEFAULT_CONFIDENCE_THRESHOLD, EvaluationResults } from "types/detectors";

import { ConfidenceSliderCharts } from "./components/ConfidenceSliderCharts";
import { ConfidenceSliderHeader } from "./components/ConfidenceSliderHeader";

function generateMarksFixed(start: number, end: number) {
  const marks = [];
  // Compute the first tick as the smallest multiple of 0.1 that's >= start.
  const firstTick = Math.ceil(start * 10) / 10;
  for (let tick = firstTick; tick <= end + 1e-8; tick += 0.1) {
    const tickRounded = parseFloat(tick.toFixed(1));
    marks.push({ value: tickRounded, label: tickRounded.toFixed(1) });
  }
  return marks;
}

export interface ConfidenceSliderProps {
  value: number;
  totalClasses: number;
  setValue: (value: number) => void;
  setValueCommitted?: (value: number) => void;
  evaluationResults?: EvaluationResults | null;
  defaultShowTradeoff?: boolean;
  hideTradeoffCharts?: boolean;
  showBottomBorder?: boolean;
}

export function ConfidenceSlider(props: ConfidenceSliderProps) {
  const {
    value = DEFAULT_CONFIDENCE_THRESHOLD,
    setValue,
    setValueCommitted,
    evaluationResults,
    defaultShowTradeoff = false,
    hideTradeoffCharts = false,
    showBottomBorder = true,
    totalClasses,
  } = props;

  const [valueTextField, setValueTextField] = useState<string>(value.toFixed(2));
  const [isChartVisible, setIsChartVisible] = useState(defaultShowTradeoff);
  const [isEditing, setIsEditing] = useState(false);

  const chartRef = useRef<HTMLDivElement>(null);

  const { show_system_accuracy: showSysAcc } = useAppSelector(getUserState);

  // Calculate slider settings based on totalClasses
  const [minConfidence, setMinConfidence] = useState(0.5);
  const maxConfidence = 1;
  const stepConfidence = 0.01;

  useEffect(() => {
    if (totalClasses > 2) setMinConfidence(parseFloat((1 / totalClasses).toFixed(2)));
    if (minConfidence > Number(valueTextField) && !isEditing) setValue(minConfidence);
  }, [totalClasses, minConfidence, valueTextField, isEditing, setValue]);

  const handleOutsideClick = useCallback((event: MouseEvent) => {
    if (chartRef.current && !chartRef.current.contains(event.target as Node)) {
      setIsChartVisible(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("click", handleOutsideClick);
    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, [handleOutsideClick]);

  // Only update the input value from the prop when not editing.
  useEffect(() => {
    if (!isEditing && value !== Number(valueTextField)) {
      setValueTextField(value.toFixed(2));
    }
  }, [value, valueTextField, isEditing]);

  // --- Editable input handlers ---
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { value } = e.target;
    const valueAsNumber = Number(value);
    const maximumNumber = Math.max(0, valueAsNumber);
    const clampedNumber = Math.min(maximumNumber, 1);
    setValueTextField(String(clampedNumber));
  };

  const handleInputBlur = () => {
    setIsEditing(false);
    const newValue = Number(valueTextField);
    if (Number.isNaN(newValue)) {
      // Reset to current valid slider value if input is invalid
      setValueTextField(value.toFixed(2));
    } else {
      // Clamp the value to the slider's range
      const clampedValue = Math.min(Math.max(newValue, minConfidence), maxConfidence);
      setValueTextField(clampedValue.toFixed(2));
      if (setValue) setValue(clampedValue);
      if (setValueCommitted) setValueCommitted(clampedValue);
    }
  };

  const sliderMarks = useMemo(() => generateMarksFixed(minConfidence, maxConfidence), [minConfidence, maxConfidence]);

  return (
    <Box ref={chartRef} className={`py-2 border-b-2 ${showBottomBorder ? "border-solid border-neutral-300" : ""}`}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <ConfidenceSliderHeader />
        <Paper elevation={0} sx={{ width: "95px" }}>
          <TextFieldInput
            data-testid="confidence-slider-input"
            value={valueTextField}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            onFocus={() => {
              setIsEditing(true);
            }}
            inputProps={{
              type: "number",
              step: stepConfidence,
              min: minConfidence,
              max: maxConfidence,
              style: { textAlign: "center" },
            }}
            variant="standard"
            valueType="numeric"
          />
        </Paper>
      </Box>

      {/* Keep this specific padding to align with charts */}
      <Box className="flex" sx={{ pl: !hideTradeoffCharts && isChartVisible ? 6 : 1, pr: 1 }}>
        <Slider
          value={value}
          min={minConfidence}
          max={maxConfidence}
          step={stepConfidence}
          valueLabelDisplay="auto"
          marks={sliderMarks}
          onChange={(_, sliderVal) => {
            if (typeof sliderVal === "number" && setValue) {
              setValue(sliderVal);
            }
            setIsChartVisible(true);
          }}
          onChangeCommitted={(_, sliderVal) => {
            if (typeof sliderVal === "number" && setValueCommitted) {
              setValueCommitted(sliderVal);
            }
            setIsChartVisible(true);
          }}
          onKeyDown={(event) => {
            event.preventDefault();
          }}
        />
      </Box>

      {!hideTradeoffCharts && showSysAcc && (
        <ConfidenceSliderCharts
          value={value}
          minConfidence={minConfidence}
          maxConfidence={maxConfidence}
          isAccordionExpanded={isChartVisible}
          handleChartVisibility={(newVal) => setIsChartVisible(newVal)}
          evaluationResults={evaluationResults}
        />
      )}
    </Box>
  );
}
